import { Calendar } from "primereact/calendar";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Icon, IconType } from "../../../../../common/icons/Icon";
import styles from "./StatisticsPeriodSelector.module.scss";

interface IStatisticsPeriodSelectorProps {
  dateFromName: string;
  dateToName: string;
  maxDate?: Date;
}

/**
 * Compact date-range filter tailored to the statistics tab. A glass pill with a
 * calendar icon and inline "Период" caption, right-aligned on desktop and full
 * width on mobile. Mirrors the form wiring of the shared RangeCalendar but with a
 * dashboard-filter look instead of the boxy form-field one.
 */
export const StatisticsPeriodSelector = ({
  dateFromName,
  dateToName,
  maxDate,
}: IStatisticsPeriodSelectorProps) => {
  const [dates, setDates] = useState<Date[] | null>(null);
  const { setValue, getValues } = useFormContext();
  const rootRef = useRef<HTMLDivElement>(null);

  // The Calendar only opens when its input is clicked. Make the whole pill —
  // icon and "Период" caption included — open it by focusing the input on any
  // click that didn't already land on the input itself.
  const openCalendar = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).tagName === "INPUT") return;
    rootRef.current?.querySelector("input")?.focus();
  };

  useEffect(() => {
    const initialDateFrom = getValues(dateFromName) || new Date();
    const initialDateTo = getValues(dateToName) || new Date();

    setDates([initialDateFrom, initialDateTo]);
  }, [dateFromName, dateToName, getValues]);

  useEffect(() => {
    if (dates && dates.length === 2) {
      setValue(dateFromName, dates[0]);
      setValue(dateToName, dates[1]);
    }
  }, [dates, dateFromName, dateToName, setValue]);

  return (
    <div className={styles.selector} ref={rootRef} onClick={openCalendar}>
      <span className={styles.icon}>
        <Icon
          type={IconType.Calendar}
          width="16px"
          height="16px"
          color="rgba(180, 178, 235, 0.9)"
        />
      </span>
      <span className={styles.caption}>Период</span>
      <Calendar
        className={styles.calendar}
        value={dates}
        onChange={(e) => setDates(e.value as Date[] | null)}
        selectionMode="range"
        readOnlyInput
        maxDate={maxDate}
        hideOnRangeSelection
        locale="bg"
      />
    </div>
  );
};
